import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Publishes a pinned (position: fixed) bar's REAL rendered height as a CSS
 * custom property on `targetRef`'s element, so the scroll content under the bar
 * can reserve exactly that much (`padding-bottom: var(--x, <fallback>)`).
 *
 * Why it exists (KAN-1069): a hand-computed reserve constant drifts from the
 * bar it guards (line-height, wrapped buttons, a note that appears) and the last
 * card hides behind the bar at scroll end. A measured value cannot drift.
 *
 * The border-box already includes the bar's own safe-area bottom padding, so
 * callers must NOT add the inset again. Re-measured via ResizeObserver;
 * the property is removed on cleanup.
 *
 * KAN-1069 follow-up: the value is the bar's height OR how far the bar's top sits above
 * the bottom of the scroll area it covers, whichever is larger. The bar's height
 * alone was not enough on the iPhone: the scroll area can end below the visible
 * screen edge, so the last card still sat under the bar. Mark the scroll area with
 * `data-cvx-bar-reserve` (the target itself, or one element inside it); without
 * that mark the hook falls back to the bar's height.
 *
 * @param {{current: ?Element}} barRef    the pinned bar.
 * @param {{current: ?HTMLElement}} targetRef  an ancestor of the element carrying the reserve.
 * @param {string} varName  e.g. '--cvx-logjob-bar-h'.
 * @param {boolean} [enabled=true]
 */
export default function useBarHeightVar(barRef, targetRef, varName, enabled = true) {
  const cleanupRef = useRef(null);
  // A child's layout effect runs BEFORE its parent's ref is attached, so on the
  // first mount `targetRef` can still be empty here (the Route Log Job: the page
  // root and the bar mount in the same commit). The layout effect tries first;
  // the passive effect - which runs after every ref is attached - tries again,
  // so the reserve is never silently skipped (KAN-1069 follow-up, the device
  // check that still failed after two fixes).
  const attach = () => {
    if (cleanupRef.current) return;
    const bar = barRef?.current;
    const target = targetRef?.current;
    if (!enabled || !bar || !target) return;
    const scroller = target.hasAttribute?.('data-cvx-bar-reserve')
      ? target
      : target.querySelector?.('[data-cvx-bar-reserve]') || null;
    const sync = () => {
      const barRect = bar.getBoundingClientRect();
      let reserve = barRect.height;
      if (scroller) {
        // Padding sits inside the scroller's box, so writing it never moves this edge (no feedback loop).
        const overlap = scroller.getBoundingClientRect().bottom - barRect.top;
        if (Number.isFinite(overlap) && overlap > reserve) reserve = overlap;
      }
      target.style.setProperty(varName, `${Math.ceil(Math.max(0, reserve))}px`);
    };
    sync();
    let ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(sync);
      ro.observe(bar, { box: 'border-box' });
      if (scroller) ro.observe(scroller, { box: 'border-box' });
    }
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    cleanupRef.current = () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      target.style.removeProperty(varName);
    };
  };
  const detach = () => {
    if (cleanupRef.current) cleanupRef.current();
    cleanupRef.current = null;
  };

  useLayoutEffect(() => {
    attach();
    return detach;
  }, [barRef, targetRef, varName, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Second chance once every ref in the commit is attached (no-op when already attached).
  useEffect(() => {
    attach();
  });
}

export { useBarHeightVar };
